import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, authFetch } from "../App";
import { useTranslation } from "../i18n/LanguageProvider";
import { toast } from "sonner";
import { Navigation, Loader2, Info } from "lucide-react";
import { offlineAuthFetch } from "../lib/offlineAuthFetch";
import { setCachedData, getCachedData } from "../lib/offlineDB";
import useDailyContext from "../hooks/useDailyContext";

// Refactored sub-components
import { getGPSPosition, GPSInfoDialog, GPSErrorDialog } from "./clock/TimeClockHelpers";
import { MobileQuickMode } from "./clock/MobileClockView";
import { DesktopStandardMode } from "./clock/DesktopClockView";

const API = '/api';

import { useNavigate, Navigate } from "react-router-dom";

export default function TimeClock() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockStatus, setClockStatus] = useState(null); // null, 'working', 'break'
  const [clockedInAt, setClockedInAt] = useState(null);
  const [breakStartedAt, setBreakStartedAt] = useState(null);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedBreakMinutes, setSelectedBreakMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [todayEntry, setTodayEntry] = useState(null);

  // Daily-Context: Einsatzplanung + Unterkunft (heute)
  const todayIso = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const { context: dailyCtx } = useDailyContext(user?.id, todayIso);
  const [autoProjectApplied, setAutoProjectApplied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [freshUser, setFreshUser] = useState(null);
  
  // Group clock state
  const [clockMode, setClockMode] = useState("single");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupClockLoading, setGroupClockLoading] = useState(false);
  const [groupClockResult, setGroupClockResult] = useState(null);
  
  // GPS state
  const [showGpsInfo, setShowGpsInfo] = useState(false);
  const [showGpsError, setShowGpsError] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsPermission, setGpsPermission] = useState("unknown");
  const [lastGpsCoords, setLastGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const gpsEnabled = user?.permissions?.gps_tracking_enabled === true;
  const gpsErrorActionRef = useRef(null);

  // Check browser GPS permission status on mount
  useEffect(() => {
    if (!gpsEnabled) return;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        setGpsPermission(result.state);
        result.onchange = () => setGpsPermission(result.state);
      }).catch(() => setGpsPermission("prompt"));
    } else {
      setGpsPermission("prompt");
    }
  }, [gpsEnabled]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Reusable data loader
  const refreshStatus = useCallback(async () => {
    try {
      const statusRes = await offlineAuthFetch(`${API}/clock/status`, {}, { cacheKey: "clock-status" });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData) {
          setTodayEntry(statusData);
          if (statusData.clock_in && !statusData.clock_out) {
            setClockedInAt(new Date(statusData.clock_in));
            setSelectedProject(statusData.project_id);
            if (statusData.on_break) {
              setClockStatus("break");
              setBreakStartedAt(new Date(statusData.break_start));
            } else {
              setClockStatus("working");
            }
            setTotalBreakSeconds(statusData.total_break_seconds || 0);
          } else {
            setClockStatus(null);
            setClockedInAt(null);
            setBreakStartedAt(null);
            setTotalBreakSeconds(0);
          }
        } else {
          setClockStatus(null);
          setClockedInAt(null);
          setBreakStartedAt(null);
          setTotalBreakSeconds(0);
          setTodayEntry(null);
        }
      }
    } catch (error) {
      console.error("Status refresh error:", error);
    }
  }, []);

  // Load projects and today's status
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, statusRes, userRes, groupsRes] = await Promise.all([
          offlineAuthFetch(`${API}/projects`, {}, { cacheKey: "projects-list" }),
          offlineAuthFetch(`${API}/clock/status`, {}, { cacheKey: "clock-status" }),
          offlineAuthFetch(`${API}/auth/me`, {}, { cacheKey: "auth-me" }),
          offlineAuthFetch(`${API}/employee-groups`, {}, { cacheKey: "employee-groups" }),
        ]);
        
        const projectsData = await projectsRes.json();
        setProjects(projectsData);
        
        if (groupsRes.ok) {
          const groupsData = await groupsRes.json();
          setGroups(groupsData);
        }
        
        let lastProjectId = null;
        if (userRes.ok) {
          const userData = await userRes.json();
          lastProjectId = userData.last_project_id;
          setFreshUser(userData);
        }
        
        const activeProjects = projectsData.filter(p => p.is_active !== false && p.status === "aktiv");
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData) {
            setTodayEntry(statusData);
            if (statusData.clock_in && !statusData.clock_out) {
              setClockedInAt(new Date(statusData.clock_in));
              setSelectedProject(statusData.project_id);
              if (statusData.on_break) {
                setClockStatus("break");
                setBreakStartedAt(new Date(statusData.break_start));
              } else {
                setClockStatus("working");
              }
              setTotalBreakSeconds(statusData.total_break_seconds || 0);
            } else {
              if (lastProjectId && activeProjects.find(p => p.id === lastProjectId)) {
                setSelectedProject(lastProjectId);
              } else if (activeProjects.length === 1) {
                setSelectedProject(activeProjects[0].id);
              } else if (activeProjects.length > 0) {
                setSelectedProject("");
              }
            }
          } else {
            if (lastProjectId && activeProjects.find(p => p.id === lastProjectId)) {
              setSelectedProject(lastProjectId);
            } else if (activeProjects.length === 1) {
              setSelectedProject(activeProjects[0].id);
            }
          }
        }
      } catch (error) {
        console.error("Fehler beim Laden:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  /* ── Auto-Vorauswahl Projekt aus Einsatzplanung (überschreibt nicht bereits aktive Stempelungen) ── */
  useEffect(() => {
    if (!dailyCtx || loading) return;
    // Bereits laufender Stempel? Nicht überschreiben
    if (clockStatus === "working" || clockStatus === "break") return;
    // Fall A: genau 1 geplantes Projekt → auto-setzen wenn noch nichts gewählt ODER aktuell nicht das geplante
    if (dailyCtx.auto_project_id && dailyCtx.auto_project_id !== selectedProject) {
      setSelectedProject(dailyCtx.auto_project_id);
      setAutoProjectApplied(true);
    }
  }, [dailyCtx, loading, clockStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab focus listener
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshStatus();
    };
    const handleFocus = () => refreshStatus();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshStatus]);

  // Calculate elapsed time
  const getElapsedTime = useCallback(() => {
    if (!clockedInAt) return "00:00:00";
    const elapsed = Math.floor((currentTime - clockedInAt) / 1000);
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [clockedInAt, currentTime]);

  // Calculate working time (minus breaks)
  const getWorkingTime = useCallback(() => {
    if (!clockedInAt) return "00:00:00";
    let breakSeconds = totalBreakSeconds;
    if (clockStatus === "break" && breakStartedAt) {
      breakSeconds += Math.floor((currentTime - breakStartedAt) / 1000);
    }
    const elapsed = Math.floor((currentTime - clockedInAt) / 1000) - breakSeconds;
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [clockedInAt, currentTime, totalBreakSeconds, clockStatus, breakStartedAt]);

  // GPS error dialog handlers
  const handleGpsRetry = () => {
    setShowGpsError(false);
    const action = gpsErrorActionRef.current;
    gpsErrorActionRef.current = null;
    if (action === "clock-in") setTimeout(() => handleClockIn(), 100);
    else if (action === "clock-out") setTimeout(() => handleClockOut(), 100);
  };
  const handleGpsCancel = () => {
    setShowGpsError(false);
    gpsErrorActionRef.current = null;
  };

  // Clock In
  const handleClockIn = async () => {
    let projectToUse = selectedProject;
    const activeProjects = projects.filter(p => p.is_active !== false && p.status === "aktiv");
    if (!projectToUse && activeProjects.length === 1) {
      projectToUse = activeProjects[0].id;
      setSelectedProject(projectToUse);
    }
    if (!projectToUse) {
      toast.error("Bitte wählen Sie ein Projekt");
      return;
    }

    if (gpsEnabled) {
      setGpsLoading(true);
      setGpsError(null);
      try {
        const location = await getGPSPosition();
        setGpsPermission("granted");
        setLastGpsCoords(location);
        setGpsLoading(false);
        await doClockIn(projectToUse, location);
      } catch (err) {
        setGpsPermission(err?.code === 1 ? "denied" : gpsPermission);
        setGpsLoading(false);
        setGpsError(err?.message || "GPS fehlgeschlagen");
        gpsErrorActionRef.current = "clock-in";
        setShowGpsError(true);
      }
      return;
    }
    await doClockIn(projectToUse, null);
  };

  const doClockIn = async (projectToUse, location) => {
    try {
      const body = { project_id: projectToUse, break_minutes: selectedBreakMinutes, location };
      const res = await offlineAuthFetch(`${API}/clock/in`, {
        method: "POST",
        body: JSON.stringify(body),
      }, {
        type: "clock-in",
        meta: { project_id: projectToUse },
        optimisticResponse: { queued: true, offline: true, clock_in: new Date().toISOString(), project_id: projectToUse },
      });
      if (res.ok) {
        const clockData = await res.json();
        if (clockData.offline) {
          toast.success("Einstempeln lokal gespeichert (wird synchronisiert)");
          setClockedInAt(new Date());
          setClockStatus("working");
          setSelectedProject(projectToUse);
          await setCachedData("clock-status-offline", { clock_in: new Date().toISOString(), project_id: projectToUse, offline: true });
        } else {
          toast.success(t("toast.eingestempelt"));
          if (clockData.auto_group_clock?.length > 0) {
            for (const g of clockData.auto_group_clock) {
              const names = g.members.map(m => m.name).join(", ");
              toast.success(`Team "${g.group_name}": ${names} automatisch mitgestempelt`, { duration: 5000 });
            }
          }
        }
        await refreshStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler beim Einstempeln");
      }
    } catch {
      toast.error("Fehler beim Einstempeln");
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    if (gpsEnabled) {
      setGpsLoading(true);
      setGpsError(null);
      try {
        const location = await getGPSPosition();
        setGpsPermission("granted");
        setLastGpsCoords(location);
        setGpsLoading(false);
        await doClockOut(location);
      } catch (err) {
        setGpsPermission(err?.code === 1 ? "denied" : gpsPermission);
        setGpsLoading(false);
        setGpsError(err?.message || "GPS fehlgeschlagen");
        gpsErrorActionRef.current = "clock-out";
        setShowGpsError(true);
      }
      return;
    }
    await doClockOut(null);
  };

  const doClockOut = async (location) => {
    try {
      const res = await offlineAuthFetch(`${API}/clock/out`, {
        method: "POST",
        body: JSON.stringify({ location }),
      }, {
        type: "clock-out",
        optimisticResponse: { queued: true, offline: true, clock_out: new Date().toISOString() },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.offline) {
          toast.success("Ausstempeln lokal gespeichert (wird synchronisiert)");
          setClockStatus(null);
          setClockedInAt(null);
          await setCachedData("clock-status-offline", null);
        } else {
          toast.success(t("toast.ausgestempelt"));
        }
        await refreshStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler beim Ausstempeln");
      }
    } catch {
      toast.error("Fehler beim Ausstempeln");
    }
  };

  // Start Break
  const handleStartBreak = async () => {
    try {
      const res = await authFetch(`${API}/clock/break/start`, { method: "POST" });
      if (res.ok) {
        setClockStatus("break");
        setBreakStartedAt(new Date());
        toast.success("Pause gestartet");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler beim Starten der Pause");
    }
  };

  // End Break
  const handleEndBreak = async () => {
    try {
      const res = await authFetch(`${API}/clock/break/end`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setClockStatus("working");
        setTotalBreakSeconds(data.total_break_seconds || 0);
        setBreakStartedAt(null);
        toast.success("Pause beendet");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler beim Beenden der Pause");
    }
  };

  // Group Clock In
  const handleGroupClockIn = async () => {
    if (!selectedGroup) { toast.error("Bitte Gruppe wählen"); return; }
    if (!selectedProject) { toast.error("Bitte Projekt wählen"); return; }
    setGroupClockLoading(true);
    try {
      let location = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
        });
        location = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
      } catch {
        // GPS not available
      }
      const res = await authFetch(`${API}/clock/group-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: selectedGroup, project_id: selectedProject, break_minutes: selectedBreakMinutes, location }),
      });
      if (res.ok) {
        const result = await res.json();
        setGroupClockResult(result);
        toast.success(`${result.count} Mitarbeiter eingestempelt${location ? " (mit GPS)" : ""}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler beim Sammelstempeln"); }
    setGroupClockLoading(false);
  };

  // Group Clock Out
  const handleGroupClockOut = async () => {
    if (!selectedGroup) { toast.error("Bitte Gruppe wählen"); return; }
    setGroupClockLoading(true);
    try {
      const res = await authFetch(`${API}/clock/group-out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: selectedGroup }),
      });
      if (res.ok) {
        const result = await res.json();
        setGroupClockResult(result);
        toast.success(`${result.count} Mitarbeiter ausgestempelt`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler beim Sammelstempeln"); }
    setGroupClockLoading(false);
  };

  const effectiveUser = freshUser || user;
  const canGroupClock = (effectiveUser?.role === "admin" || effectiveUser?.role === "projektleiter") ||
    groups.some(g => g.leader_id === effectiveUser?.id);

  // Navigation popup state
  const [showNavPopup, setShowNavPopup] = useState(false);
  const [navProject, setNavProject] = useState(null);

  // Wrap doClockIn to show nav popup after success
  const doClockInWithNav = async (projectToUse, location) => {
    await doClockIn(projectToUse, location);
    // Show navigation popup if enabled
    const proj = projects.find(p => p.id === projectToUse);
    const navEnabled = effectiveUser?.nav_auto_after_clockin !== false && effectiveUser?.nav_popup_enabled !== false;
    if (proj && navEnabled) {
      const addr = proj.address && typeof proj.address === "object" ? proj.address : null;
      const hasCoords = addr?.lat && addr?.lng;
      const hasAddress = addr?.street || addr?.city;
      if (hasCoords || hasAddress) {
        setNavProject(proj);
        setShowNavPopup(true);
      }
    }
  };

  // Override handleClockIn to use doClockInWithNav
  const handleClockInWithNav = async () => {
    let projectToUse = selectedProject;
    const activeProjects = projects.filter(p => p.is_active !== false && p.status === "aktiv");
    if (!projectToUse && activeProjects.length === 1) {
      projectToUse = activeProjects[0].id;
      setSelectedProject(projectToUse);
    }
    if (!projectToUse) {
      toast.error("Bitte wählen Sie ein Projekt");
      return;
    }

    if (gpsEnabled) {
      setGpsLoading(true);
      setGpsError(null);
      try {
        const location = await getGPSPosition();
        setGpsPermission("granted");
        setLastGpsCoords(location);
        setGpsLoading(false);
        await doClockInWithNav(projectToUse, location);
      } catch (err) {
        setGpsPermission(err?.code === 1 ? "denied" : gpsPermission);
        setGpsLoading(false);
        setGpsError(err?.message || "GPS fehlgeschlagen");
        gpsErrorActionRef.current = "clock-in";
        setShowGpsError(true);
      }
      return;
    }
    await doClockInWithNav(projectToUse, null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Stundenzettel-User: keine Stempeluhr — synchron weiterleiten (nach allen Hooks)
  if (user?.time_tracking_mode === "stundenzettel") {
    return <Navigate to="/app/m/stundenkonto" replace />;
  }

  // Mobile Quick Mode
  if (isMobile) {
    return (
      <>
        <GPSErrorDialog open={showGpsError} onRetry={handleGpsRetry} onCancel={handleGpsCancel} />
        <GPSInfoDialog open={showGpsInfo} onClose={() => setShowGpsInfo(false)} t={t} />
        {gpsEnabled && (
          <div className="space-y-0">
            {gpsPermission === "denied" ? (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border-b border-red-500/20" data-testid="gps-denied-banner">
                <Navigation className="w-3.5 h-3.5 text-red-400 shrink-0" />
                <span className="text-xs text-red-400 font-medium flex-1">GPS blockiert — Bitte in Browsereinstellungen aktivieren</span>
              </div>
            ) : gpsPermission === "granted" ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border-b border-green-500/20" data-testid="gps-active-banner">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-green-400 font-medium flex-1">GPS aktiv</span>
                {gpsLoading && <Loader2 className="w-3.5 h-3.5 text-green-400 animate-spin" />}
                <button onClick={() => setShowGpsInfo(true)} className="text-green-400 hover:text-green-300 p-0.5" data-testid="gps-info-btn">
                  <Info className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-4 py-1 bg-amber-500/5 border-b border-amber-500/10" data-testid="gps-required-banner">
                <Navigation className="w-3 h-3 text-amber-400/60 shrink-0" />
                <span className="text-[10px] text-amber-400/60">GPS wird beim Stempeln abgefragt</span>
              </div>
            )}
            {lastGpsCoords && (
              <div className="px-4 py-1 bg-white/[0.03] border-b border-white/5" data-testid="gps-coords-debug">
                <p className="text-[10px] text-gray-500 font-mono">
                  GPS: {lastGpsCoords.lat.toFixed(5)}, {lastGpsCoords.lng.toFixed(5)} (±{Math.round(lastGpsCoords.accuracy)}m)
                </p>
              </div>
            )}
            {gpsError && (
              <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20" data-testid="gps-error-debug">
                <p className="text-[10px] text-red-400 font-medium">GPS Fehler: {gpsError}</p>
              </div>
            )}
          </div>
        )}
        {/* Standard-Stempeluhr */}
        <MobileQuickMode
          currentTime={currentTime}
          clockStatus={clockStatus}
          clockedInAt={clockedInAt}
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          selectedBreakMinutes={selectedBreakMinutes}
          setSelectedBreakMinutes={setSelectedBreakMinutes}
          handleClockIn={handleClockInWithNav}
          handleClockOut={handleClockOut}
          user={effectiveUser}
          todayEntry={todayEntry}
          showNavPopup={showNavPopup}
          navProject={navProject}
          onCloseNavPopup={() => { setShowNavPopup(false); setNavProject(null); }}
          dailyCtx={dailyCtx}
          autoProjectApplied={autoProjectApplied}
        />
      </>
    );
  }

  // Desktop Standard Mode
  return (
    <>
      <GPSErrorDialog open={showGpsError} onRetry={handleGpsRetry} onCancel={handleGpsCancel} />
      <GPSInfoDialog open={showGpsInfo} onClose={() => setShowGpsInfo(false)} t={t} />
      <DesktopStandardMode
        currentTime={currentTime}
        clockStatus={clockStatus}
        clockedInAt={clockedInAt}
        projects={projects}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        handleClockIn={handleClockIn}
        handleClockOut={handleClockOut}
        handleStartBreak={handleStartBreak}
        handleEndBreak={handleEndBreak}
        getElapsedTime={getElapsedTime}
        getWorkingTime={getWorkingTime}
        todayEntry={todayEntry}
        user={effectiveUser}
        gpsEnabled={gpsEnabled}
        gpsLoading={gpsLoading}
        gpsPermission={gpsPermission}
        onShowGpsInfo={() => setShowGpsInfo(true)}
        clockMode={clockMode}
        setClockMode={setClockMode}
        groups={groups}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        handleGroupClockIn={handleGroupClockIn}
        handleGroupClockOut={handleGroupClockOut}
        groupClockLoading={groupClockLoading}
        groupClockResult={groupClockResult}
        setGroupClockResult={setGroupClockResult}
        canGroupClock={canGroupClock}
        selectedBreakMinutes={selectedBreakMinutes}
        setSelectedBreakMinutes={setSelectedBreakMinutes}
        dailyCtx={dailyCtx}
        autoProjectApplied={autoProjectApplied}
      />
    </>
  );
}
